/**
 * Security center (Phase J).
 *
 * Backend endpoints (under /api/charger/ocpp/...):
 *   GET    /security/:chargerId
 *   GET    /security/:chargerId/events
 *   POST   /security/:chargerId/provision
 *   POST   /security/:chargerId/rotate
 *   POST   /security/:chargerId/escalate
 *   GET    /security/:chargerId/certificates
 *   POST   /security/:chargerId/certificates
 *   DELETE /security/:chargerId/certificates
 *   POST   /security/:chargerId/certificates/sign
 *   POST   /security/:chargerId/certificates/signed
 *
 * Notes:
 *  - `POST /rotate` is gated behind OCPP_CSMS_ROTATION_READY=1 and
 *    returns HTTP 503 until that env var is set in production.
 *  - `POST /rotate` and `POST /escalate` return HTTP 202 with
 *    `{ op_id, state }`; the UI should switch to polling
 *    `GET /security/:chargerId` every 10–15s while the op is non-terminal.
 */

export type SecurityProfile = 1 | 2 | 3;

export type SecurityEventSeverity = 'info' | 'warning' | 'critical';

export type AuthorizationKeyStatus =
  | 'provisioned'
  | 'active'
  | 'rotating'
  | 'rotation_failed';

export type SecurityOpType =
  | 'rotate_authorization_key'
  | 'escalate_security_profile'
  | 'install_certificate'
  | 'delete_certificate'
  | 'sign_certificate_csr'
  | 'push_signed_certificate'
  | 'provision'
  | string;

export type SecurityOpState =
  | 'pending'
  | 'pre_staging'
  | 'sent_to_charger'
  | 'awaiting_reconnect'
  | 'verifying'
  | 'rolling_back'
  | 'completed'
  | 'rolled_back'
  | 'failed';

export const TERMINAL_SECURITY_OP_STATES: ReadonlyArray<SecurityOpState> = [
  'completed',
  'rolled_back',
  'failed',
];

export interface SecurityOp {
  id: number;
  op_type: SecurityOpType;
  state: SecurityOpState;
  from_value: string | null;
  to_value: string | null;
  started_at: string;
  completed_at: string | null;
  last_error: string | null;
  metadata: unknown;
}

export interface SecurityEvent {
  id: number;
  charger_uuid?: string;
  charge_point_id?: string | null;
  event_type: string;
  severity: SecurityEventSeverity;
  tech_info: string | null;
  op_id: number | null;
  certificate_id: number | null;
  received_at: string;
}

export type CertificateType =
  | 'V2GRootCertificate'
  | 'MORootCertificate'
  | 'CSMSRootCertificate'
  | 'ManufacturerRootCertificate'
  | 'ChargingStationCertificate'
  | string;

export interface Certificate {
  id: number;
  certificate_type: CertificateType;
  serial_number: string;
  hash_algorithm: string;
  not_before: string;
  not_after: string;
  installed_at: string;
  deleted_at: string | null;
}

export interface SecurityOverview {
  success: boolean;
  charger_uuid: string;
  charge_point_id: string | null;
  security_profile: SecurityProfile | number;
  security_profile_target: SecurityProfile | number | null;
  authorization_key_status: AuthorizationKeyStatus;
  authorization_key_last_rotated_at: string | null;
  active_ops: SecurityOp[];
  recent_events: SecurityEvent[];
  certificates: Certificate[];
}

export interface ListSecurityEventsResponse {
  success: boolean;
  events: SecurityEvent[];
}

export interface ProvisionBody {
  initial_security_profile?: SecurityProfile;
}

export interface RotateAuthorizationKeyBody {
  new_authorization_key: string;
}

export interface EscalateBody {
  target_profile: 2 | 3;
}

export interface OpAcceptedResponse {
  success: boolean;
  op_id: number;
  state: SecurityOpState;
}

export interface ListCertificatesResponse {
  success: boolean;
  certificates: Certificate[];
}

export interface InstallCertificateBody {
  certificate_type: CertificateType;
  certificate: string;
  not_before?: string;
  not_after?: string;
  serial_number?: string;
}

export interface InstallCertificateResponse {
  success: boolean;
  status: 'Accepted' | 'Rejected' | string;
  cert_id?: number;
  message?: string;
}

export interface DeleteCertificateBody {
  hash_algorithm?: 'SHA256' | string;
  issuer_name_hash: string;
  issuer_key_hash: string;
  serial_number: string;
}

export interface SignCsrBody {
  csr: string;
  certificate_type?: CertificateType;
}

export interface PushSignedCertificateBody {
  certificate_chain: string;
  certificate_type?: CertificateType;
}

/**
 * Pure helper: returns true while a security op is still in-flight.
 * Used by the SecurityTab polling loop.
 */
export const isSecurityOpInFlight = (state: SecurityOpState): boolean => {
  return !TERMINAL_SECURITY_OP_STATES.includes(state);
};
